import { DataTableColumn } from '@/components/UI/DataTable/DataTable.model.ts';
import { DateTimePickerNative } from '@/components/UI/DateTimePickerNative/DateTimePickerNative.tsx';
import { Duration, normalize as normalizeDuration } from '@/components/UI/FormElements/Duration/Duration.tsx';
import Radio from '@/components/UI/FormElements/Radio/Radio.tsx';
import Switch from '@/components/UI/FormElements/Switch';
import { errorToast } from '@/helpers.tsx';
import { _notNil } from '@/littledash.ts';
import cn from 'classnames';
import type { DataTableColumnFillRequestV1, DataTableColumnFillType } from '@/model/DataTable.model.ts';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal.tsx';
import { roundToNearestHours } from 'date-fns';
import { FC } from 'react';
import { Controller, useForm, Validate } from 'react-hook-form@latest';
import { parse as parseDuration } from 'tinyduration';

interface FormData {
  fillMode: 'planned' | 'manual';
  start: string;
  duration: string;
  overwrite: boolean;
}

const durationLabelByFillType: Record<DataTableColumnFillType, string> = {
  pk_dose: 'Time between doses',
  sample_collected_at: 'Time between collections',
  sample_dose_time: 'Time between doses',
};
const durationValidator: Validate<string, FormData> = (value) => {
  try {
    const parsedDuration = parseDuration(value);
    const nonZero = Object.values(parsedDuration).some((value) => value > 0);
    if (!nonZero) {
      return 'Duration cannot be zero';
    }
  } catch (e) {
    return 'Invalid duration';
  }
  return true;
};

interface DataTableColumnFillProps {
  column: DataTableColumn;
  fillType: DataTableColumnFillType;
  onFill: (data: DataTableColumnFillRequestV1) => Promise<void>;
  onCancel: () => void;
}

export const DataTableColumnFill: FC<DataTableColumnFillProps> = ({ column, fillType, onFill, onCancel }) => {
  const formMethods = useForm<FormData>({
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues: {
      fillMode: 'planned',
      start: roundToNearestHours(new Date()).toISOString(),
      duration:
        column.type === 'timestampBaseline' && _notNil(column.pk?.interval)
          ? normalizeDuration({ minutes: Math.floor(column.pk.interval / 60) })
          : 'PT30M',
      overwrite: false,
    },
  });
  const fillMode = formMethods.watch('fillMode');
  const showManualFields = fillType !== 'sample_collected_at' || fillMode === 'manual';
  const handleSubmit = async (formData: FormData) => {
    try {
      const usePlannedTiming = fillType === 'sample_collected_at' && formData.fillMode === 'planned';
      const manualTimingFields = usePlannedTiming
        ? {}
        : {
            start: formData.start,
            duration: formData.duration,
          };
      await onFill({
        type: fillType,
        overwrite: formData.overwrite,
        ...manualTimingFields,
      });
    } catch (e) {
      errorToast('Could not fill column');
      return Promise.reject(e);
    }
  };
  return (
    <ModalContainer size="narrow">
      <ModalHeader title="Fill column" />
      <form
        onSubmit={formMethods.handleSubmit(handleSubmit)}
        data-test-component="DataTableColumnFill"
        data-test-element="form"
      >
        {fillType === 'sample_collected_at' && (
          <Controller
            control={formMethods.control}
            name="fillMode"
            render={({ field }) => (
              <div className="pa3" data-test-element="fill-mode-field">
                <Radio
                  id="fill-mode-planned"
                  value="planned"
                  name={field.name}
                  label="Use planned time"
                  checked={field.value === 'planned'}
                  onChange={() => field.onChange('planned')}
                />
                <Radio
                  id="fill-mode-manual"
                  value="manual"
                  name={field.name}
                  label="Define start and intervals"
                  checked={field.value === 'manual'}
                  onChange={() => field.onChange('manual')}
                />
              </div>
            )}
          />
        )}
        {showManualFields && (
          <>
            <Controller
              control={formMethods.control}
              name="start"
              shouldUnregister
              render={({ field }) => (
                <div
                  className={cn('pa3 flex justify-between flex-wrap items-center align-center', {
                    'bt b--moon-gray': fillType === 'sample_collected_at',
                  })}
                  data-test-element="start-field"
                >
                  <label>Start time</label>
                  <DateTimePickerNative
                    style={{ marginBottom: 0 }}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                </div>
              )}
            />
            <Controller
              control={formMethods.control}
              name="duration"
              shouldUnregister
              rules={{ validate: durationValidator }}
              render={({ field, fieldState }) => (
                <div
                  className="pa3 bt b--moon-gray flex justify-between flex-wrap items-center align-center"
                  data-test-element="duration-field"
                >
                  <label>{durationLabelByFillType[fillType]}</label>
                  <Duration
                    value={field.value}
                    disabled={field.disabled}
                    error={fieldState.invalid}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                </div>
              )}
            />
          </>
        )}
        <Controller
          control={formMethods.control}
          name="overwrite"
          render={({ field }) => (
            <div
              className="pa3 bt b--moon-gray flex justify-between flex-wrap items-center align-center"
              data-test-element="overwrite-field"
            >
              <span>
                <label>Overwrite existing values</label>
                <small>All timestamps in this column will be replaced</small>
              </span>
              <Switch value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
            </div>
          )}
        />
      </form>
      <ModalActions
        className="pa3 bt b--moon-gray"
        onSubmit={formMethods.handleSubmit(handleSubmit)}
        submitBtnText="Fill"
        submitButtonProps={{
          disabled: !formMethods.formState.isValid && formMethods.formState.isDirty,
        }}
        onCancel={onCancel}
        cancelBtnText="Cancel"
      />
    </ModalContainer>
  );
};
